import { CheckCircle, Award } from 'lucide-react';
import PageWrapper from '@/components/PageWrapper';
import ImageCarousel from '@/components/ImageCarousel';
import { FadeUp, SlideLeft, SlideRight, StaggerContainer, StaggerItem } from '@/components/ScrollReveal';

const ComprehensivePage = () => {
  const approachFeatures = [
    'Individually planned sessions based on how your body moves',
    'Classical Pilates principles adapted to your needs',
    'Taught by a STOTT PILATES® certified teacher',
    'Evidence-based method grounded in biomechanics',
  ];

  const images = [
    { src: '/images/TEASER HERO.jpg', alt: 'Teaser', caption: 'The Teaser' },
    { src: '/images/LADDER BARREL TREE HERO.jpg', alt: 'Ladder Barrel', caption: 'Ladder Barrel' },
    { src: '/images/CHAIR HORSEBACK SIDE.jpg', alt: 'Chair', caption: 'Chair Work' },
    { src: '/images/REFORMER TENDON.jpg', alt: 'Reformer', caption: 'Reformer' },
  ];

  return (
    <PageWrapper>
      {/* Hero */}
      <section data-testid="comprehensive-hero" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">Services</p>
              <h1 className="font-heading text-5xl sm:text-6xl text-stone-900 leading-[1.1] tracking-tight">
                Comprehensive <span className="italic">Approach</span>
              </h1>
              <p className="mt-8 text-stone-600 text-lg leading-relaxed font-light">
                A holistic methodology grounded in science and classical principles.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="image-zoom aspect-[4/3]">
                <img
                  src="/images/LADDER BARREL BACKBEND.jpg"
                  alt="Comprehensive Approach"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <StaggerContainer className="grid md:grid-cols-2 gap-6">
            {approachFeatures.map((feature, index) => (
              <StaggerItem key={index}>
                <div className="flex items-start gap-4 p-6 bg-white border border-stone-200">
                  <CheckCircle className="text-stone-900 flex-shrink-0 mt-1" size={20} strokeWidth={1.5} />
                  <p className="text-stone-600 leading-relaxed font-light">{feature}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Image Carousel */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <ImageCarousel images={images} />
          </FadeUp>
        </div>
      </section>

      {/* Approach Details */}
      <section data-testid="approach-details" className="py-24 md:py-32 bg-stone-900 text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <SlideLeft>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">Our Philosophy</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-white leading-[1.1] tracking-tight">
                Comprehensive <span className="italic">Training</span>
              </h2>
              <p className="mt-8 text-stone-300 leading-relaxed font-light">
                Grounded in a deep understanding of how the body moves — not just how exercises look. 
                Sessions are carefully planned using classical Pilates principles and adapted to suit 
                your individual body type, movement patterns, and goals.
              </p>
            </SlideLeft>

            <SlideRight>
              <div className="image-zoom aspect-[4/3]">
                <img
                  src="/images/ELEPHANT CHAIR.jpg"
                  alt="Training"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </SlideRight>
          </div>
        </div>
      </section>

      {/* STOTT Method */}
      <section data-testid="stott-method" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <SlideLeft>
              <div className="relative image-zoom aspect-[4/3]">
                <img
                  src="/images/otto.jpeg"
                  alt="STOTT Method"
                  className="w-full h-full object-cover editorial-image"
                />
                <div className="absolute bottom-4 left-4 bg-white p-4 flex items-center gap-3">
                  <Award size={20} strokeWidth={1.5} className="text-stone-900" />
                  <span className="text-xs uppercase tracking-wider text-stone-900">STOTT PILATES®</span>
                </div>
              </div>
            </SlideLeft>

            <SlideRight>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">Certification</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-stone-900 leading-[1.1] tracking-tight">
                STOTT PILATES® <span className="italic">Method</span>
              </h2>
              <p className="mt-8 text-stone-600 leading-relaxed font-light">
                STOTT PILATES® is internationally recognised as one of the most rigorous Pilates 
                education systems. The method is rooted in modern exercise science and biomechanics, 
                emphasising spinal alignment, breath, and efficient movement patterns.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <span className="px-4 py-2 text-xs uppercase tracking-wider text-stone-600 border border-stone-300">
                  Evidence-Based
                </span>
                <span className="px-4 py-2 text-xs uppercase tracking-wider text-stone-600 border border-stone-300">
                  Biomechanics
                </span>
                <span className="px-4 py-2 text-xs uppercase tracking-wider text-stone-600 border border-stone-300">
                  Spinal Alignment
                </span>
              </div>
            </SlideRight>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
};

export default ComprehensivePage;
